import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { CollectionContact } from '../model';
import { ContactEmailAction } from '../hooks/useToggleCollectionClientContactEmail';

interface Props {
  contacts: CollectionContact[];
  recipientEmails: string[];
  onToggleContact: (conIdNo: string, action: ContactEmailAction) => Promise<void>;
  disabled?: boolean;
}

const buildRecipientSet = (recipientEmails: string[]): Set<string> =>
  new Set(recipientEmails.map((email) => email.toLowerCase()));

const contactIsSelected = (contact: CollectionContact, recipientSet: Set<string>): boolean =>
  contact.emails.some((email) => recipientSet.has(email.toLowerCase()));

interface ContactCardProps {
  contact: CollectionContact;
  recipientSet: Set<string>;
  disabled?: boolean;
  onRequestToggle: (contact: CollectionContact, action: ContactEmailAction) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({ contact, recipientSet, disabled, onRequestToggle }) => {
  const selected = contactIsSelected(contact, recipientSet);
  const canToggle = !!contact.con_id_no && contact.emails.length > 0 && !disabled;

  const handleClick = () => {
    if (!canToggle || !contact.con_id_no) return;
    onRequestToggle(contact, selected ? 'remove' : 'add');
  };

  return (
    <Box
      role={canToggle ? 'button' : undefined}
      tabIndex={canToggle ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (!canToggle) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        borderRadius: 1,
        p: 1.5,
        height: '100%',
        cursor: canToggle ? 'pointer' : 'default',
        '&:hover': canToggle ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {contact.name || '—'}
          </Typography>
          {contact.job_title && (
            <Typography variant="caption" color="text.secondary">
              {contact.job_title}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
          {contact.is_primary === 'A' && <Chip label="Primary" size="small" color="success" />}
          {contact.is_billing_primary === 'A' && <Chip label="Billing" size="small" color="primary" />}
          {contact.inactive && <Chip label="Inactive" size="small" variant="outlined" />}
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mt: 1 }}>
        {contact.phones.length > 0 ? contact.phones.join(' · ') : 'No phone'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
        {contact.emails.length > 0 ? (
          contact.emails.map((email) => <Chip key={email} label={email} size="small" />)
        ) : (
          <Chip label="No email" size="small" color="error" variant="outlined" />
        )}
      </Box>
    </Box>
  );
};

const CollectionClientContactsCard: React.FC<Props> = ({ contacts, recipientEmails, onToggleContact, disabled }) => {
  const [pendingContact, setPendingContact] = useState<CollectionContact | null>(null);
  const [pendingAction, setPendingAction] = useState<ContactEmailAction | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const recipientSet = buildRecipientSet(recipientEmails);

  const handleRequestToggle = (contact: CollectionContact, action: ContactEmailAction) => {
    setPendingContact(contact);
    setPendingAction(action);
  };

  const handleCloseDialog = () => {
    if (isConfirming) return;
    setPendingContact(null);
    setPendingAction(null);
  };

  const handleConfirm = async () => {
    if (!pendingContact?.con_id_no || !pendingAction) return;

    setIsConfirming(true);
    try {
      await onToggleContact(pendingContact.con_id_no, pendingAction);
      setPendingContact(null);
      setPendingAction(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const dialogTitle =
    pendingAction === 'remove'
      ? 'Remove from collection emails?'
      : 'Add to collection emails?';

  const dialogBody =
    pendingContact && pendingAction ? (
      <>
        {pendingAction === 'remove' ? 'Remove' : 'Add'}{' '}
        <strong>{pendingContact.name || 'this contact'}</strong>
        {pendingAction === 'remove' ? ' from' : ' to'} collection notices?
        {pendingContact.emails.length > 0 && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {pendingContact.emails.map((email) => (
              <li key={email}>
                <Typography variant="body2">{email}</Typography>
              </li>
            ))}
          </Box>
        )}
      </>
    ) : null;

  if (!contacts || contacts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={1.5}>
        {contacts.map((contact, index) => (
          <Grid item xs={12} md={4} key={contact.con_id_no || `contact-${index}`}>
            <ContactCard
              contact={contact}
              recipientSet={recipientSet}
              disabled={disabled || isConfirming}
              onRequestToggle={handleRequestToggle}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={pendingContact !== null} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{dialogBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isConfirming}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" disabled={isConfirming} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CollectionClientContactsCard;
